import React, { useState, useEffect, useRef, useMemo } from 'react';
import { SWAP_TOKENS } from '../mock';
import { useCryptoPrices } from '../hooks/useCryptoPrices';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import ScrollReveal from '../components/ScrollReveal';
import {
  ArrowDownUp, ChevronDown, Search, ShieldCheck, Zap, Info, TrendingUp, TrendingDown,
  Wallet, Lock, ArrowRight, Sparkles, Activity, Globe, Clock, Layers, Radio, ExternalLink,
} from 'lucide-react';

// Token logo with gradient ring frame
const TokenLogo = ({ token, size = 40, ring = true }) => (
  <div className={`relative shrink-0 rounded-full ${ring ? 'p-[2px]' : ''}`}
    style={ring ? { background: `linear-gradient(135deg, ${token.color}, ${token.color}88, transparent)` } : {}}>
    <div className="rounded-full overflow-hidden bg-white flex items-center justify-center" style={{ width: size, height: size }}>
      <img src={token.logo} alt={token.symbol} className="w-full h-full object-contain"
        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
      <div className="hidden absolute inset-0 items-center justify-center font-bold text-white text-xs" style={{ background: token.color, borderRadius: '9999px' }}>
        {token.symbol.slice(0, 3)}
      </div>
    </div>
  </div>
);

const CG_IDS = SWAP_TOKENS.map(t => t.cgId);

const TokenSelector = ({ token, onSelect, exclude, live }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => SWAP_TOKENS.filter(t =>
    t.symbol !== exclude && (t.symbol.toLowerCase().includes(q.toLowerCase()) || t.name.toLowerCase().includes(q.toLowerCase()))
  ), [q, exclude]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="flex items-center gap-2 bg-white hover:bg-neutral-50 border border-neutral-200 hover:border-[#8247E5] transition-colors rounded-xl px-3 h-12">
          <TokenLogo token={token} size={30} ring={false} />
          <div className="text-left">
            <div className="font-semibold text-sm leading-tight">{token.symbol}</div>
            <div className="text-[10px] text-neutral-500 leading-tight">{token.network}</div>
          </div>
          <ChevronDown className="w-4 h-4 text-neutral-400 ml-1" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md p-0">
        <DialogHeader className="p-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#8247E5]" /> Select an asset</DialogTitle>
        </DialogHeader>
        <div className="p-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or symbol" className="pl-10 h-11 bg-neutral-50" />
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto px-2 pb-4">
          {filtered.map(t => {
            const price = live?.[t.cgId]?.price ?? t.usd;
            const change = live?.[t.cgId]?.change24h;
            return (
              <button key={t.symbol} onClick={() => { onSelect(t); setOpen(false); setQ(''); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-violet-50 text-left transition-colors">
                <TokenLogo token={t} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{t.symbol}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-neutral-100 rounded text-neutral-600">{t.network}</span>
                  </div>
                  <div className="text-xs text-neutral-500 truncate">{t.name}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-semibold text-neutral-900">${price < 1 ? price.toFixed(4) : price.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  {change !== undefined && (
                    <div className={`text-[10px] font-semibold ${change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AnimatedNumber = ({ value }) => {
  const [display, setDisplay] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = performance.now();
    const startVal = from.current;
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - start) / 500);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(startVal + (value - startVal) * eased);
      if (p < 1) raf = requestAnimationFrame(step);
      else from.current = value;
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [value]);
  const d = value === 0 ? 2 : value >= 100 ? 2 : value >= 1 ? 4 : 6;
  return <span>{display.toFixed(d)}</span>;
};

const RateRefreshRing = ({ seconds = 15, onRefresh, live }) => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf;
    let lastCycle = 0;
    const tick = (now) => {
      const elapsed = (now - start) / 1000;
      const p = (elapsed % seconds) / seconds;
      setProgress(p);
      const cycle = Math.floor(elapsed / seconds);
      if (cycle > lastCycle) { lastCycle = cycle; onRefresh?.(); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seconds, onRefresh]);
  const r = 9, c = 2 * Math.PI * r;
  return (
    <div className="flex items-center gap-2 text-xs text-neutral-500">
      <svg width="24" height="24" viewBox="0 0 24 24" className="shrink-0">
        <circle cx="12" cy="12" r={r} stroke="#e5e7eb" strokeWidth="2" fill="none" />
        <circle cx="12" cy="12" r={r} stroke="#8247E5" strokeWidth="2" fill="none"
          strokeDasharray={c} strokeDashoffset={c * (1 - progress)}
          strokeLinecap="round" transform="rotate(-90 12 12)" />
      </svg>
      <span className={live ? 'text-emerald-600 font-medium' : ''}>
        {live ? 'Live' : 'Loading rates'}
      </span>
    </div>
  );
};

const SwapPage = () => {
  const [from, setFrom] = useState(SWAP_TOKENS[0]);
  const [to, setTo] = useState(SWAP_TOKENS[1]);
  const [amount, setAmount] = useState('0.1');
  const [rateType, setRateType] = useState('fixed');
  const [address, setAddress] = useState('');
  const [rotating, setRotating] = useState(false);
  const { data: live, status } = useCryptoPrices(CG_IDS);
  const { toast } = useToast();

  const fromPrice = live?.[from.cgId]?.price ?? from.usd;
  const toPrice = live?.[to.cgId]?.price ?? to.usd;

  const rate = useMemo(() => fromPrice / toPrice, [fromPrice, toPrice]);

  const receive = useMemo(() => {
    const a = parseFloat(amount) || 0;
    const fee = rateType === 'fixed' ? 0.005 : 0.003;
    return a * rate * (1 - fee);
  }, [amount, rate, rateType]);

  const flip = () => {
    setRotating(true);
    setTimeout(() => setRotating(false), 500);
    setFrom(to); setTo(from);
  };

  const submit = () => {
    if (!address || address.length < 10) { toast({ title: 'Enter a valid recipient address' }); return; }
    if (!amount || parseFloat(amount) <= 0) { toast({ title: 'Enter an amount' }); return; }
    toast({ title: 'Swap order created', description: `You will receive ≈ ${receive.toFixed(6)} ${to.symbol}. Redirecting to deposit…` });
  };

  return (
    <div className="relative overflow-hidden dark:bg-neutral-950">
      <div className="absolute inset-0 pointer-events-none dark:hidden" style={{
        background: 'radial-gradient(1000px circle at 15% 10%, #F3EBFF 0%, transparent 50%), radial-gradient(800px circle at 85% 90%, #FCE7F3 0%, transparent 50%)'
      }} />
      <div className="absolute inset-0 pointer-events-none hidden dark:block" style={{
        background: 'radial-gradient(1000px circle at 15% 10%, rgba(130,71,229,0.35) 0%, transparent 50%), radial-gradient(800px circle at 85% 90%, rgba(168,85,247,0.28) 0%, transparent 50%)'
      }} />
      <div className="glow-ring animate-blob" style={{ width: 400, height: 400, background: '#A855F7', top: '10%', left: '-5%', opacity: 0.35 }} />
      <div className="glow-ring animate-blob" style={{ width: 350, height: 350, background: '#EC4899', bottom: '10%', right: '-3%', opacity: 0.25, animationDelay: '3s' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-neutral-900/70 border border-violet-200 dark:border-violet-500/40 text-xs font-medium text-violet-700 dark:text-[#C084FC] mb-4 shadow-sm">
            <span className={`w-1.5 h-1.5 rounded-full ${status === 'ready' ? 'bg-emerald-500 pulse-glow' : 'bg-amber-500'}`} />
            {status === 'ready' ? 'Live rates from CoinGecko' : 'Connecting to price feed…'}
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold">Swap <span className="gradient-text">any asset,</span> instantly</h1>
          <p className="text-neutral-600 mt-4 max-w-2xl mx-auto leading-relaxed">
            Trade 20+ cryptos in minutes. No account, no email, no waiting. Just enter your wallet address and we handle the rest, with live rates straight from the market.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* MAIN SWAP CARD */}
          <div className="lg:col-span-2">
            <div className="relative p-[1.5px] rounded-3xl"
              style={{ background: 'linear-gradient(135deg, #8247E5, #A855F7, #EC4899, #8247E5)', backgroundSize: '300% 300%', animation: 'shine 8s linear infinite' }}>
              <div className="bg-white dark:bg-neutral-900 rounded-3xl p-6 md:p-8">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                  <div className="inline-flex bg-neutral-100 dark:bg-neutral-800 rounded-xl p-1">
                    {[
                      { id: 'fixed', label: 'Fixed rate', icon: Lock },
                      { id: 'float', label: 'Float rate', icon: Activity },
                    ].map(o => (
                      <button key={o.id} onClick={() => setRateType(o.id)}
                        data-testid={`rate-${o.id}`}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                          rateType === o.id
                            ? 'bg-white dark:bg-[#8247E5] text-[#8247E5] dark:text-white shadow-sm'
                            : 'text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white'
                        }`}>
                        <o.icon className="w-3.5 h-3.5" /> {o.label}
                      </button>
                    ))}
                  </div>
                  <RateRefreshRing seconds={60} live={status === 'ready'} />
                </div>

                {/* SEND */}
                <div className="bg-neutral-50 dark:bg-neutral-800/60 rounded-2xl p-5 border border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">You send</span>
                    <span className="text-xs text-neutral-500">Balance in vault: {from.reserve.toLocaleString(undefined, { maximumFractionDigits: 2 })} {from.symbol}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00"
                      className="flex-1 min-w-0 bg-transparent border-0 outline-none font-display text-3xl md:text-4xl font-bold text-neutral-900 placeholder:text-neutral-300" />
                    <TokenSelector token={from} onSelect={setFrom} exclude={to.symbol} live={live} />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm text-neutral-500">≈ ${((parseFloat(amount) || 0) * fromPrice).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    <div className="flex gap-1">
                      {[0.001, 0.01, 0.1, 1].map(v => (
                        <button key={v} onClick={() => setAmount(String(v))} className="text-xs px-2 py-1 bg-white hover:bg-violet-50 hover:text-[#8247E5] border border-neutral-200 rounded-md transition-colors">{v}</button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Flip button */}
                <div className="relative flex justify-center -my-2 z-10">
                  <button onClick={flip}
                    className="w-11 h-11 rounded-xl bg-white dark:bg-neutral-800 border-2 border-neutral-200 dark:border-violet-500/40 hover:border-[#8247E5] hover:bg-violet-50 dark:hover:bg-violet-900/40 shadow-md transition-all"
                    style={{ transform: rotating ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <ArrowDownUp className="w-4 h-4 mx-auto text-[#8247E5]" />
                  </button>
                </div>

                {/* GET */}
                <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-violet-900/40 dark:to-fuchsia-900/30 rounded-2xl p-5 border border-violet-200 dark:border-violet-600/40">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-[#8247E5] dark:text-[#C084FC] uppercase tracking-wider">You get</span>
                    <span className="text-xs text-neutral-500 dark:text-neutral-400">Balance in vault: {to.reserve.toLocaleString(undefined, { maximumFractionDigits: 2 })} {to.symbol}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0 font-display text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white">
                      <AnimatedNumber value={receive} />
                    </div>
                    <TokenSelector token={to} onSelect={setTo} exclude={from.symbol} live={live} />
                  </div>
                  <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">≈ ${(receive * toPrice).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                </div>

                {/* Rate details */}
                <div className="mt-5 bg-neutral-50 dark:bg-neutral-800/60 rounded-xl p-4 space-y-2 text-sm border border-transparent dark:border-neutral-700">
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-600 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Exchange rate</span>
                    <span className="font-mono font-semibold">1 {from.symbol} = {rate.toFixed(6)} {to.symbol}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-600 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Service fee</span>
                    <span className="font-semibold">{rateType === 'fixed' ? '0.5%' : '0.3%'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-600 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Estimated settlement</span>
                    <span className="font-semibold">5 – 15 min</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-600 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Min / Max</span>
                    <span className="font-mono text-xs">0.001 / {(from.reserve * 0.5).toFixed(2)} {from.symbol}</span>
                  </div>
                </div>

                {/* Recipient */}
                <div className="mt-5">
                  <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> {to.symbol} recipient address</label>
                  <Input value={address} onChange={(e) => setAddress(e.target.value)}
                    placeholder={`Your ${to.symbol} (${to.network}) wallet address…`}
                    className="h-12 font-mono text-sm" />
                </div>

                {/* CTA */}
                <Button onClick={submit}
                  className="w-full h-14 mt-5 shine-btn text-white font-bold text-base rounded-2xl shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50 transition-shadow">
                  Create exchange <ArrowRight className="w-4 h-4 ml-1" />
                </Button>

                <div className="mt-4 flex items-center justify-center gap-4 text-xs text-neutral-500 flex-wrap">
                  <div className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Non-custodial</div>
                  <div className="flex items-center gap-1"><Zap className="w-3 h-3" /> Wallet to wallet</div>
                  <div className="flex items-center gap-1"><Lock className="w-3 h-3" /> No KYC</div>
                </div>
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="space-y-4">
            {/* Live prices */}
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-sm flex items-center gap-2"><Radio className="w-4 h-4 text-[#8247E5]" /> Live prices</h3>
                <span className="text-[10px] text-emerald-600 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> {status === 'ready' ? 'LIVE' : '—'}</span>
              </div>
              <div className="space-y-3">
                {SWAP_TOKENS.slice(0, 6).map(t => {
                  const l = live?.[t.cgId];
                  const price = l?.price ?? t.usd;
                  const change = l?.change24h;
                  const priceStr = price >= 1 ? price.toLocaleString(undefined, { maximumFractionDigits: 2 }) : price.toFixed(4);
                  const up = (change ?? 0) >= 0;
                  return (
                    <div key={t.symbol} className="flex items-center gap-3">
                      <TokenLogo token={t} size={30} ring={false} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-semibold">{t.symbol}</span>
                            <span className="text-[10px] text-neutral-500">{t.name}</span>
                          </div>
                          <span className="text-xs font-semibold">${priceStr}</span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-[10px] text-neutral-500">Vault: {t.reserve < 100 ? t.reserve.toFixed(2) : t.reserve.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                          {change !== undefined && (
                            <span className={`text-[10px] font-semibold ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {up ? '▲' : '▼'} {Math.abs(change).toFixed(2)}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <a href="https://www.coingecko.com/" target="_blank" rel="noreferrer" className="mt-4 flex items-center justify-center gap-1 text-[10px] text-neutral-400 hover:text-[#8247E5] transition-colors">
                Price data by CoinGecko <ExternalLink className="w-2.5 h-2.5" />
              </a>
            </div>

            {/* Recent swaps */}
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-[#8247E5]" /> Recent exchanges</h3>
                <span className="text-[10px] text-neutral-500">Last hour</span>
              </div>
              <RecentSwapsFeed />
            </div>

            {/* Trust */}
            <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-5 text-white relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
              <ShieldCheck className="w-6 h-6 mb-3" />
              <div className="font-display font-bold text-lg leading-tight">Non-custodial by design</div>
              <div className="text-sm text-white/80 mt-1 leading-relaxed">Your funds route directly to your recipient wallet. TokenGate never takes custody at any point in the exchange.</div>
            </div>
          </div>
        </div>

        {/* Features */}
        <ScrollReveal>
          <div className="mt-16 grid md:grid-cols-4 gap-4">
            {[
              { icon: Zap,          title: 'Wallet to wallet', desc: 'Every exchange settles peer-to-peer, without a middleman holding your balance.' },
              { icon: ShieldCheck,  title: 'No KYC ever',       desc: 'No sign-up, no identity checks, no waiting for approvals.' },
              { icon: Globe,        title: '20+ major assets',  desc: 'The most-used networks and the stablecoins that move markets.' },
              { icon: Sparkles,     title: 'Real market rates', desc: 'Prices are pulled live from CoinGecko’s global aggregate feed.' },
            ].map((f, i) => (
              <div key={i} className="bg-white/70 dark:bg-neutral-900/60 backdrop-blur rounded-2xl border border-violet-100 dark:border-violet-500/20 p-5 hover:border-violet-300 dark:hover:border-violet-400/50 hover:-translate-y-1 hover:shadow-xl hover:shadow-violet-500/10 transition-all">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: 'linear-gradient(135deg, #8247E520, #A855F720)' }}>
                  <f.icon className="w-4 h-4 text-[#8247E5] dark:text-[#C084FC]" />
                </div>
                <div className="font-semibold text-neutral-900 dark:text-white">{f.title}</div>
                <div className="text-xs text-neutral-600 dark:text-neutral-300 mt-1 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </div>
  );
};

const RecentSwapsFeed = () => {
  const [swaps, setSwaps] = useState(() => generateSwaps(5));
  useEffect(() => {
    const id = setInterval(() => setSwaps(prev => [generateSwaps(1)[0], ...prev].slice(0, 5)), 5000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="space-y-2.5">
      {swaps.map((s, i) => (
        <div key={s.id} className="flex items-center gap-2 text-xs" style={{ animation: i === 0 ? 'ticker-in .4s ease' : 'none' }}>
          <TokenLogo token={s.from} size={22} ring={false} />
          <span className="font-mono font-semibold text-neutral-800">{s.fromAmt}</span>
          <ArrowRight className="w-3 h-3 text-neutral-400" />
          <TokenLogo token={s.to} size={22} ring={false} />
          <span className="font-mono font-semibold text-neutral-800">{s.toAmt}</span>
          <span className="ml-auto text-neutral-400">{s.time}s</span>
        </div>
      ))}
    </div>
  );
};

const generateSwaps = (n) => {
  const out = [];
  for (let i = 0; i < n; i++) {
    const from = SWAP_TOKENS[Math.floor(Math.random() * SWAP_TOKENS.length)];
    let to = SWAP_TOKENS[Math.floor(Math.random() * SWAP_TOKENS.length)];
    if (to.symbol === from.symbol) to = SWAP_TOKENS[(SWAP_TOKENS.indexOf(from) + 1) % SWAP_TOKENS.length];
    const fromAmt = (Math.random() * 5).toFixed(from.usd > 1000 ? 4 : 2);
    const toAmt = ((fromAmt * from.usd) / to.usd).toFixed(to.usd > 1000 ? 4 : 2);
    out.push({ id: Math.random().toString(36).slice(2), from, to, fromAmt, toAmt, time: Math.floor(Math.random() * 90) + 2 });
  }
  return out;
};

export default SwapPage;
